import React, { useCallback, useEffect, useState } from 'react'
import { Container } from './styles'
import { CalendarFilter, MedicalEventTypeFilter, MedicalViewSelector } from '~/components/shared/medicalViews'
import { TimelineSelector, ModalSelectPregnancy, ChangePregnancyButton } from '~/components/maternalInfantMedicalView'
import { EMedicalViews, IHealthProcedures } from '~/interfaces'
import { useMaternalInfantHealthProcedures, usePatient } from '~/context'
import { filterHealthProceduresTypesMaternalInfant } from '~/constants/healthProceduresTypes'
import { SearchHealthProcedure } from '~/components/healthProcedures/SearchHealthProcedure'
import { useMaternalInfantViewSettings } from '~/hooks'
import { hasObjectValue } from '~/utils'
import { Inline } from '~/styles'

const OPEN = true
const CLOSED = false

export const Header = (): JSX.Element => {
  const { patient } = usePatient()

  const {
    getPatientPregnancies,
    handleFilterHealthProceduresPerType,
    handleFilterHealthProceduresPerDate,
    gestationHealthProcedures,
    hasDateFiltersActive,
    dateFiltersActive,
    typeFiltersActive,
    hasTypeFiltersActive,
    puerperiumHealthProcedures,
    neonateHealthProcedures,
    infantHealthProcedures,
    isMotherTimelineSelected,
    filterHealthProceduresByTerm,

    updateSelectedPregnancy,
    updateSelectedBaby,
    updateSelectedTimeline
  } = useMaternalInfantHealthProcedures()

  const { thereAreMaternalInfantViewSettings, getMaternalInfantViewSettings } = useMaternalInfantViewSettings()

  const [visibilityPregnancySelection, setVisibilityPregnancySelection] = useState<boolean>(CLOSED)
  const openPregnancySelection = () => setVisibilityPregnancySelection(OPEN)
  const closePregnancySelection = () => setVisibilityPregnancySelection(CLOSED)

  const getMotherHealthProcedures = (): IHealthProcedures[] => {
    if (gestationHealthProcedures && puerperiumHealthProcedures) {
      return [...gestationHealthProcedures, ...puerperiumHealthProcedures]
    }
    return []
  }

  const getPregnancyHealthProcedures = (): IHealthProcedures[] => {
    if (neonateHealthProcedures && infantHealthProcedures && gestationHealthProcedures) {
      return [...neonateHealthProcedures, ...infantHealthProcedures, ...gestationHealthProcedures]
    }
    return []
  }

  const parseOptionsToSearchProcedures = (): IHealthProcedures[] => {
    if (isMotherTimelineSelected) {
      return getMotherHealthProcedures()
    }

    return getPregnancyHealthProcedures()
  }

  useEffect(() => {
    const hasPatient = hasObjectValue({ ...patient })
    if (hasPatient) getPatientPregnancies({ patientId: patient.id })
  }, [getPatientPregnancies, patient])

  const handleChangePregnancy = (): void => {
    openPregnancySelection()
  }

  const updateMaternalInfantViewSettingsFromStorage = useCallback(() => {
    const { selectedPregnancy, selectedBaby, selectedTimeline } = getMaternalInfantViewSettings()
    updateSelectedPregnancy(selectedPregnancy)
    if (selectedBaby) updateSelectedBaby(selectedBaby)
    updateSelectedTimeline(selectedTimeline)
  }, [getMaternalInfantViewSettings, updateSelectedBaby, updateSelectedPregnancy, updateSelectedTimeline])

  useEffect(() => {
    if (thereAreMaternalInfantViewSettings) {
      updateMaternalInfantViewSettingsFromStorage()
      return
    }

    openPregnancySelection()
  }, [thereAreMaternalInfantViewSettings, updateMaternalInfantViewSettingsFromStorage])

  return (
    <Container>
      <Inline width="100%" justifyContent="space-between">
        <TimelineSelector />

        <Inline spacing="0.5rem">
          <ChangePregnancyButton onClick={handleChangePregnancy} />
          <MedicalViewSelector selectedMedicalView={EMedicalViews.MATERNAL_INFANT} isActive />

          <ModalSelectPregnancy show={visibilityPregnancySelection} onCloseModal={closePregnancySelection} />

          <MedicalEventTypeFilter
            options={filterHealthProceduresTypesMaternalInfant}
            selected={typeFiltersActive}
            isActive={hasTypeFiltersActive}
            onChange={handleFilterHealthProceduresPerType}
          />

          <CalendarFilter
            dates={dateFiltersActive}
            isActive={hasDateFiltersActive}
            onChange={handleFilterHealthProceduresPerDate}
          />
        </Inline>
      </Inline>

      <SearchHealthProcedure options={parseOptionsToSearchProcedures()} handleSearch={filterHealthProceduresByTerm} />
    </Container>
  )
}
